import type { DemoOp, DemoScene, ReplicaState, SceneId, SpaceTab } from "./types.ts";

export function cloneState(state: ReplicaState): ReplicaState {
  return structuredClone(state) as ReplicaState;
}

export function applyOp(state: ReplicaState, op: DemoOp): ReplicaState {
  switch (op.op) {
    case "typeComposer":
      return { ...state, composer: { text: op.text, typing: false } };
    case "setComposer":
      return { ...state, composer: { text: op.text, typing: op.typing === true } };
    case "pushMessage":
      return { ...state, messages: [...state.messages, op.message] };
    case "replaceMessage":
      return {
        ...state,
        messages: state.messages.map((message) => (message.id === op.id ? op.message : message))
      };
    case "removeMessage":
      return { ...state, messages: state.messages.filter((message) => message.id !== op.id) };
    case "setAgent":
      return {
        ...state,
        agents: state.agents.map((agent) =>
          agent.id === op.id ? { ...agent, status: op.status, statusLabel: op.statusLabel } : agent
        )
      };
    case "setHudSummary":
      return { ...state, hud: { ...state.hud, summary: op.summary } };
    case "pushFileActivity":
      return {
        ...state,
        hud: {
          ...state.hud,
          fileActivities: [op.activity, ...state.hud.fileActivities.filter((item) => item.id !== op.activity.id)]
        }
      };
    case "setView":
      return {
        ...state,
        view: op.view,
        ...(op.space ? { space: { ...state.space, ...op.space } } : {})
      };
    case "setActive":
      return { ...state, active: { ...state.active, ...op.active } };
    case "setDirectChat":
      return {
        ...state,
        directChats: state.directChats.map((chat) =>
          chat.id === op.id ? { ...chat, ...op.patch } : chat
        )
      };
    case "pushMeetingLine":
      if (!state.meeting) return state;
      return {
        ...state,
        meeting: {
          ...state.meeting,
          lines: [...state.meeting.lines, op.line]
        }
      };
    case "setMeeting":
      if (!state.meeting) return state;
      return { ...state, meeting: { ...state.meeting, ...op.patch } };
  }
}

function typeProgress(at: number, duration: number, playT: number, text: string): { text: string; typing: boolean } {
  if (duration <= 0 || playT >= at + duration) {
    return { text, typing: false };
  }
  const progress = Math.max(0, Math.min(1, (playT - at) / duration));
  const count = Math.max(0, Math.round(progress * text.length));
  return { text: text.slice(0, count), typing: progress < 1 };
}

export function sceneClock(scene: DemoScene, elapsedMs: number, reducedMotion: boolean): number {
  if (reducedMotion) return scene.duration;
  const loop = scene.duration + scene.hold;
  if (loop <= 0) return 0;
  return elapsedMs % loop;
}

export function stateAt(scene: DemoScene, elapsedMs: number, reducedMotion = false): ReplicaState {
  const playT = Math.min(sceneClock(scene, elapsedMs, reducedMotion), scene.duration);
  let state = cloneState(scene.initial);
  for (const item of scene.timeline) {
    if (item.op.op === "typeComposer") {
      if (playT < item.at) break;
      const typed = typeProgress(item.at, item.op.duration, playT, item.op.text);
      state = { ...state, composer: typed };
      if (playT < item.at + item.op.duration) break;
      continue;
    }
    if (item.at > playT) break;
    state = applyOp(state, item.op);
  }
  return state;
}

export function finishedState(scene: DemoScene): ReplicaState {
  return stateAt(scene, scene.duration, true);
}

export function sceneById(
  scenes: Readonly<Record<SceneId, DemoScene>>,
  sceneId: SceneId
): DemoScene {
  const scene = scenes[sceneId];
  if (!scene) throw new Error(`Unknown demo scene: ${sceneId}`);
  return scene;
}

export function spaceSceneForTab(current: SceneId, tab: SpaceTab): SceneId {
  if (tab === "documents") return "project-files";
  const fromDirect =
    current === "intake-dm" || current === "knowledge-draft" || current === "delivery-dm";
  if (fromDirect || current === "project-files") {
    return current === "delivery-dm" || current === "project-files" ? "finalize-thread" : "team-thread";
  }
  return current;
}

export function applyTab(state: ReplicaState, tab: SpaceTab): ReplicaState {
  const view: ReplicaState["view"] =
    tab === "documents" ? "files" : tab === "messages" ? "messages" : "threads";
  return {
    ...state,
    view,
    space: { ...state.space, tab },
    active: {
      ...state.active,
      files: tab === "documents",
      threadId: tab === "threads" || tab === "messages" || tab === "documents" ? null : state.active.threadId,
      agentId: tab === "documents" || tab === "threads" || tab === "messages" ? null : state.active.agentId,
      personId: null,
      directChatId: null
    }
  };
}
